import { useState } from 'react';
import axios from 'axios';
import { useMutation } from '@redwoodjs/web';

import { Downloading } from '@mui/icons-material';
import { Box, BoxProps, Typography } from '@mui/material';

import CheckUploaded from 'src/assets/images/icons/CheckUploadedSVG';
import FileIcon from 'src/assets/images/icons/FileIconSVG';
import { REGENERATE_PRESIGNED_URL } from 'src/graphql/mutations/generateAwsURL.mutation';
import { downloadDocument } from 'src/utils/utils';

import { Flex } from '../Flex';

export type File = {
    size?: string;
    fileName: string;
    url?: string;
};

interface DocumentUploadedPreview {
    label?: string;
    file: File;
    boxProps?: BoxProps;
}

export const DocumentUploadedPreview: React.FC<DocumentUploadedPreview> = ({
    file,
    label,
    boxProps,
}) => {
    const [regenerateUrl] = useMutation(REGENERATE_PRESIGNED_URL);
    const [isDownloadingDocument, setIsDownloadingDocument] = useState(false);

    const handleDownload = async () => {
        setIsDownloadingDocument(true);
        const downloadOptions = {
            fileUrl: file?.url,
            fileName: file?.fileName ?? label,
            isDownloadingToBrowser: true,
        };
        await downloadDocument(
            downloadOptions,
            async (url: string) => {
                const { data } = await regenerateUrl({ variables: { url } });
                const presignedUrl = data?.regenerateSecureAWSUrl;
                return await axios.get(presignedUrl, { responseType: 'blob' });
            }
        );
        setIsDownloadingDocument(false);
    };

    return (
        <Box width="100%" {...boxProps}>
            {label && (
                <Typography marginBottom="8px" fontSize="14px" fontWeight="600" lineHeight="21px">
                    {label}
                </Typography>
            )}
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                padding="14px"
                border="1px solid #F3A256"
                borderRadius="8px"
            >
                <Flex justifyContent="center" alignItems="center">
                    {(() => {
                        if (!isDownloadingDocument) {
                            return <FileIcon cursor="pointer" onClick={handleDownload} />;
                        }
                        return <Downloading />;
                    })()}
                    <Flex flex={1} marginLeft="14px" flexDirection="column" overflow="hidden">
                        <Typography
                            fontSize="14px"
                            lineHeight="20px"
                            fontWeight="500"
                            color="#1D1D1B"
                            sx={{ wordBreak: 'break-all' }}
                        >
                            {file.fileName || label}
                        </Typography>
                        <Typography
                            fontSize="14px"
                            lineHeight="20px"
                            fontWeight="400"
                            color="#8E8E8D"
                        >
                            {file.size || ''}
                        </Typography>
                    </Flex>
                </Flex>
                <CheckUploaded />
            </Box>
        </Box>
    );
};
